import Image from 'next/image'
import Link from 'components/shared/link'
import { Globe } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface FederationRowCardProps {
    federation: IFederation & {
        mostRecentMatchDate?: string | null // ISO string from server
        mostRecentSeasonStartDate?: string | null // ISO string from server
        matchCount?: number
        competitionCount?: number
        teamCount?: number
        seasonCount?: number
    }
}

/**
 * FederationRowCard - Full-width row card for federations
 * 
 * Displays federation information in a horizontal row layout
 * Shows most recent activity (matches or season start date from competitions)
 * Only shows the federation logo (no second logo like competitions)
 */
const FederationRowCard = ({ federation }: FederationRowCardProps) => {
    // Determine the most recent activity date
    // Dates come as ISO strings from server, convert to Date objects for comparison
    const matchDate = federation.mostRecentMatchDate ? new Date(federation.mostRecentMatchDate) : null
    const seasonDate = federation.mostRecentSeasonStartDate ? new Date(federation.mostRecentSeasonStartDate) : null
    
    // Get the most recent date (compare both if they exist)
    const mostRecentDate = matchDate && seasonDate
        ? (matchDate.getTime() > seasonDate.getTime() ? matchDate : seasonDate)
        : (matchDate || seasonDate)
    
    // Format the activity date
    const activityText = mostRecentDate && !isNaN(mostRecentDate.getTime())
        ? formatDistanceToNow(mostRecentDate, { addSuffix: true })
        : 'No recent activity'

    // Build compact stats string for single line
    const statsParts: string[] = []
    if (federation.region) statsParts.push(federation.region)
    if (federation.competitionCount && federation.competitionCount > 0) {
        statsParts.push(`${federation.competitionCount} competition${federation.competitionCount !== 1 ? 's' : ''}`)
    }
    if (federation.teamCount && federation.teamCount > 0) {
        statsParts.push(`${federation.teamCount} team${federation.teamCount !== 1 ? 's' : ''}`)
    }
    if (federation.seasonCount && federation.seasonCount > 0) {
        statsParts.push(`${federation.seasonCount} season${federation.seasonCount !== 1 ? 's' : ''}`)
    }
    if (federation.matchCount && federation.matchCount > 0) {
        statsParts.push(`${federation.matchCount} match${federation.matchCount !== 1 ? 'es' : ''}`)
    }
    const statsLine = statsParts.join(' · ')
    const activityLine = mostRecentDate ? `Last activity ${activityText}` : null

    // Link component automatically prepends the language, so don't include it in the href
    return (
        <Link href={`/federation/${federation.id}/competitions`} className='block w-full max-w-full overflow-hidden'>
            <div className='group relative w-full border border-border rounded-md md:rounded-lg bg-card hover:bg-muted/50 transition-colors cursor-pointer overflow-hidden flex items-start sm:items-center max-w-full min-w-0 min-h-[72px] py-2'>
                {/* Federation Logo Section - Compact square on left, fallback when no logo */}
                <div className='relative flex-shrink-0 bg-muted border-r border-border w-14 h-14 sm:w-16 sm:h-16 min-w-[56px] sm:min-w-[64px] flex items-center justify-center'>
                    {federation.logo ? (
                        <Image
                            src={federation.logo}
                            alt={federation.name}
                            fill
                            className='object-contain p-1.5'
                            sizes="64px"
                        />
                    ) : (
                        <Globe className='w-7 h-7 sm:w-8 sm:h-8 text-muted-foreground/50' />
                    )}
                </div>

                {/* Main Content Section - Compact single column */}
                <div className='flex items-center justify-between gap-2 sm:gap-3 flex-1 min-w-0 px-3 sm:px-4'>
                    <div className='flex-1 min-w-0 overflow-hidden'>
                        <h3 className='font-semibold text-sm sm:text-base truncate group-hover:text-primary transition-colors'>
                            {federation.name}
                        </h3>
                        {(statsLine || activityLine) && (
                            <p className='text-xs text-muted-foreground mt-0.5 break-words sm:truncate'>
                                {[statsLine, activityLine].filter(Boolean).join(' · ')}
                            </p>
                        )}
                    </div>

                    {/* Arrow Indicator */}
                    <div className='flex-shrink-0 text-muted-foreground group-hover:text-primary transition-colors w-4 h-4 sm:w-5 sm:h-5'>
                        <svg className='w-full h-full' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                        </svg>
                    </div>
                </div>
            </div>
        </Link>
    )
}

export default FederationRowCard
